import React, { useCallback, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import {
  type AntennaDetection,
  type BridgeDevice,
  type ReaderInfo,
  type ReaderJob,
  type ReaderStatus,
  applyAntennaMap,
  detectAntennas,
  discoverBridges,
  readdressBridge,
  resetReader,
  setReaderDetector,
  setReaderPower,
} from "../../services/readerApi";

/**
 * The hardware-changing half of the ticket reader panel, behind a disclosure
 * so the common path (test scan / force scan) stays uncluttered.
 *
 * Every control here can leave a box worse than it found it, so each one says
 * what it costs before you press it:
 *
 *   - a power change is PERMANENT (the reader's temporary-power command is not
 *     implemented on fleet firmware), which is how a box ends up stuck at the
 *     wrong power after a site visit — so we surface the previous value and a
 *     one-click restore rather than pretending the change is reversible
 *   - re-addressing reboots the bridge AND has to change the box's own
 *     pointer; the server does both halves in one call and reports each, and
 *     it is disabled server-side by default
 *
 * The UI never pre-checks the server's kill switches — it lets the attempt
 * happen and shows the refusal. That way flipping a flag in app_config takes
 * effect with no frontend redeploy.
 *
 * **Every control here acts on ONE reader**, named by `reader`. A box can
 * carry two or three, and they are separate hardware: setting power on reader
 * 1 leaves readers 2 and 3 exactly as they were, and re-addressing one bridge
 * must not disturb the others' addresses. The panel therefore always says
 * which reader it is about rather than letting "the reader" stand in for a box
 * that has several.
 */
interface ReaderAdvancedProps {
  scuName: string;
  status: ReaderStatus | null;
  /** The reader every control here acts on. Null on a box whose readers could
   *  not be read, in which case the server picks the lowest-numbered active
   *  one — the pre-multi-reader behaviour. */
  reader?: ReaderInfo | null;
  disabled: boolean;
  onDone: (job: ReaderJob | null) => void;
}

const POWER_CHOICES = [5, 10, 15, 20, 25, 30, 33];

export const ReaderAdvanced: React.FC<ReaderAdvancedProps> = ({
  scuName,
  status,
  reader = null,
  disabled,
  onDone,
}) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [dbm, setDbm] = useState(30);
  // What the ports read before our last change, so we can offer a restore —
  // the only way back, since every power write on this firmware is permanent.
  const [previousDbm, setPreviousDbm] = useState<number | null>(null);
  const [detectorDb, setDetectorDb] = useState(5);
  const [devices, setDevices] = useState<BridgeDevice[] | null>(null);
  // Which subnet the sweep covered, and which readers it therefore missed.
  const [discoverNote, setDiscoverNote] = useState<string | null>(null);
  const [ants, setAnts] = useState<AntennaDetection | null>(null);
  const [newIp, setNewIp] = useState("");
  const [readdressMac, setReaddressMac] = useState<string | null>(null);

  const run = useCallback(
    async (label: string, fn: () => Promise<ReaderJob | null>, ok: string): Promise<void> => {
      if (!token) return;
      setBusy(label);
      setError(null);
      setNote(null);
      try {
        const job = await fn();
        setNote(ok);
        onDone(job);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(null);
      }
    },
    [token, onDone],
  );

  if (!open) {
    return (
      <button
        type="button"
        className="text-xs text-primary-600 hover:underline"
        onClick={() => setOpen(true)}
      >
        Advanced reader controls…
      </button>
    );
  }

  const anyBusy = disabled || busy !== null;
  // Which reader every control below acts on. Sent with each request, so the
  // server never has to guess, and shown in the heading so the operator never
  // has to either.
  const rid = reader?.reader_id;
  // Per-reader display values, falling back to the flat status fields for a
  // single-reader box or a server that predates `readers[]`.
  const powerSummary = reader?.power_summary ?? status?.power_summary;
  const detectorNow = reader?.detector_db ?? status?.detector_db;
  const readerIp = reader?.ip_address ?? status?.ip_address;

  return (
    <div className="mt-3 border-t border-secondary-200 pt-3 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-secondary-500">
          Advanced{rid != null ? ` · reader ${rid}` : ""}
        </span>
        <button
          type="button"
          className="text-xs text-secondary-500 hover:underline"
          onClick={() => setOpen(false)}
        >
          hide
        </button>
      </div>

      {error && (
        <div className="text-xs text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          {error}
        </div>
      )}
      {note && (
        <div className="text-xs text-success-800 bg-success-50 border border-success-200 px-3 py-2 rounded">
          {note}
        </div>
      )}

      {/* RF power */}
      <div>
        <div className="text-xs font-medium text-secondary-700 mb-1">
          RF power {powerSummary ? `(now ${powerSummary})` : ""}
          {rid != null ? ` · reader ${rid}` : ""}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="text-sm border border-secondary-300 rounded px-2 py-1"
            value={dbm}
            onChange={(e) => setDbm(Number(e.target.value))}
            disabled={anyBusy}
          >
            {POWER_CHOICES.map((p) => (
              <option key={p} value={p}>
                {p} dBm
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="secondary"
            disabled={anyBusy}
            onClick={() =>
              void run(
                "power",
                async () => {
                  const job = await setReaderPower(token as string, scuName, dbm, rid);
                  const was = job.result?.before_dbm;
                  if (was && was.length) setPreviousDbm(was[0]);
                  return job;
                },
                `RF power set to ${dbm} dBm${
                  rid != null ? ` on reader ${rid}` : ""
                } — this persists until it is set back`,
              )
            }
          >
            {busy === "power" ? "Setting…" : "Set power"}
          </Button>
          {previousDbm != null && previousDbm !== dbm && (
            <Button
              size="sm"
              variant="secondary"
              disabled={anyBusy}
              onClick={() =>
                void run(
                  "power",
                  () => setReaderPower(token as string, scuName, previousDbm, rid),
                  `RF power restored to ${previousDbm} dBm`,
                )
              }
            >
              Restore {previousDbm} dBm
            </Button>
          )}
        </div>
        <p className="text-[11px] text-warning-800 mt-1">
          This change is <strong>permanent</strong> until someone sets it back — the reader&apos;s
          temporary-power command does not work on this firmware. That is how a box ends up
          stuck at the wrong power after a site visit, so note the original value
          {powerSummary ? ` (currently ${powerSummary})` : ""} before changing it.
          {rid != null
            ? ` This affects reader ${rid} only — the box's other readers keep their own power.`
            : ""}
        </p>
      </div>

      {/* Antenna detect threshold */}
      <div>
        <div className="text-xs font-medium text-secondary-700 mb-1">
          Antenna-detect threshold
          {detectorNow != null ? ` (now ${detectorNow} dB)` : ""}
          {rid != null ? ` · reader ${rid}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            max={255}
            className="text-sm border border-secondary-300 rounded px-2 py-1 w-20"
            value={detectorDb}
            onChange={(e) => setDetectorDb(Number(e.target.value))}
            disabled={anyBusy}
          />
          <span className="text-xs text-secondary-500">dB · reader default is 5</span>
          <Button
            size="sm"
            variant="secondary"
            disabled={anyBusy}
            onClick={() =>
              void run(
                "detector",
                () => setReaderDetector(token as string, scuName, detectorDb, rid),
                `Antenna-detect threshold set to ${detectorDb} dB`,
              )
            }
          >
            Set
          </Button>
        </div>
      </div>

      {/* Reader reset */}
      <div>
        <div className="text-xs font-medium text-secondary-700 mb-1">
          Reset reader{rid != null ? ` ${rid}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={anyBusy}
            onClick={() =>
              void run(
                "reset",
                () => resetReader(token as string, scuName, rid),
                `Reset sent${
                  rid != null ? ` to reader ${rid}` : ""
                } — it is down for ~10-15s`,
              )
            }
          >
            {busy === "reset" ? "Resetting…" : "Reset"}
          </Button>
          <span className="text-[11px] text-secondary-500">
            ~10-15s outage. A force scan usually beats this — it resets the reader too, and
            leaves the box scanning again afterwards.
          </span>
        </div>
      </div>

      {/* Antenna map */}
      <div>
        <div className="text-xs font-medium text-secondary-700 mb-1">
          Antennas{rid != null ? ` · reader ${rid}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={anyBusy}
            onClick={() =>
              void run(
                "ants",
                async () => {
                  setAnts(await detectAntennas(token as string, scuName, rid));
                  return null;
                },
                "Antenna check complete",
              )
            }
          >
            {busy === "ants" ? "Checking…" : "Check antennas"}
          </Button>
          <span className="text-[11px] text-secondary-500">
            asks {rid != null ? `reader ${rid}` : "the reader"} which of its ports
            actually have an antenna attached
          </span>
        </div>

        {ants && (
          <div className="mt-2 text-xs">
            <div className="text-secondary-700">
              Reader reports <strong>{ants.detected_count}</strong> attached
              {ants.detected.length > 0 && (
                <span className="text-secondary-500"> (ports {ants.detected.join(", ")})</span>
              )}
            </div>

            {ants.warning && (
              <div className="mt-1 text-warning-800 bg-warning-50 border border-warning-200 px-2 py-1 rounded">
                {ants.warning}
              </div>
            )}

            {!ants.warning &&
            ants.would_activate.length === 0 &&
            ants.would_deactivate.length === 0 ? (
              <div className="mt-1 text-success-700">
                The box already matches the reader — nothing to change.
              </div>
            ) : null}

            {ants.would_activate.length > 0 && (
              <div className="mt-1 text-secondary-700">
                Would <strong>enable</strong> {ants.would_activate.join(", ")} — attached but the
                box is not scanning them.
              </div>
            )}
            {ants.would_deactivate.length > 0 && (
              <div className="mt-1 text-secondary-700">
                Would <strong>disable</strong> {ants.would_deactivate.join(", ")} — scanned but
                nothing attached. An empty port still completes its round, so each one costs
                time on every scan.
              </div>
            )}

            {!ants.warning &&
              (ants.would_activate.length > 0 || ants.would_deactivate.length > 0) && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    disabled={anyBusy}
                    onClick={() =>
                      void run(
                        "ants-apply",
                        async () => {
                          const job = await applyAntennaMap(token as string, scuName, rid);
                          setAnts(await detectAntennas(token as string, scuName, rid));
                          return job;
                        },
                        "Antenna map written to the box",
                      )
                    }
                  >
                    {busy === "ants-apply" ? "Applying…" : "Update antennas on the box"}
                  </Button>
                  <p className="text-[11px] text-secondary-500 mt-1">
                    Changes which antennas the scanner walks
                    {rid != null ? ` on reader ${rid}` : ""}. The previous set is recorded on
                    the job so it can be put back.
                    {rid != null
                      ? " Port numbers restart at 1 on every reader, so these are this reader's ports."
                      : ""}
                  </p>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Bridge discovery + re-address */}
      <div>
        <div className="text-xs font-medium text-secondary-700 mb-1">Find readers on the box LAN</div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={anyBusy}
            onClick={() =>
              void run(
                "discover",
                async () => {
                  const res = await discoverBridges(token as string, scuName, rid);
                  setDevices(res.devices);
                  setDiscoverNote(res.note ?? null);
                  return null;
                },
                "Search complete",
              )
            }
          >
            {busy === "discover" ? "Searching…" : "Search"}
          </Button>
          <span className="text-[11px] text-secondary-500">
            Finds a reader even on the wrong subnet — the recovery path when its address is
            unknown or wrong.
          </span>
        </div>

        {/* The sweep is a directed broadcast, so it covers ONE subnet: the
            selected reader's. On a multi-reader box whose readers sit on
            different subnets, a bridge on another reader's network simply will
            not answer this search. */}
        {discoverNote && (
          <div className="text-[11px] text-secondary-600 mt-2">{discoverNote}</div>
        )}

        {devices && devices.length === 0 && (
          <div className="text-xs text-secondary-600 mt-2">
            Nothing answered. If the reader is powered, it is on a network the box cannot
            reach at all.
          </div>
        )}

        {devices && devices.length > 0 && (
          <div className="mt-2 space-y-2">
            <p className="text-[11px] text-secondary-500">
              Replies are broadcast, so this can include devices on other networks. Confirm by
              MAC before changing anything.
            </p>
            {devices.map((d) => (
              <div key={d.mac} className="text-xs border border-secondary-200 rounded p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono">{d.mac}</span>
                  <span className="text-secondary-700">{d.ip}</span>
                  <span className="text-secondary-500">{d.name}</span>
                  {!d.is_usr_bridge && (
                    <span className="text-warning-700">not a reader bridge</span>
                  )}
                  {d.ip === readerIp && (
                    <span className="text-success-700">
                      {rid != null ? `reader ${rid}` : "this box's reader"}
                    </span>
                  )}
                  <button
                    type="button"
                    className="text-primary-600 hover:underline"
                    onClick={() => {
                      setReaddressMac(d.mac);
                      setNewIp(readerIp ?? "");
                    }}
                    disabled={anyBusy}
                  >
                    re-address
                  </button>
                </div>

                {readdressMac === d.mac && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input
                      className="text-sm border border-secondary-300 rounded px-2 py-1 w-40"
                      placeholder="new IP"
                      value={newIp}
                      onChange={(e) => setNewIp(e.target.value)}
                      disabled={anyBusy}
                    />
                    <Button
                      size="sm"
                      disabled={anyBusy || !newIp.trim()}
                      onClick={() =>
                        void run(
                          "readdress",
                          () =>
                            readdressBridge(
                              token as string,
                              scuName,
                              { mac: d.mac, ip: newIp.trim() },
                              rid,
                            ),
                          `Re-addressed ${d.mac} to ${newIp.trim()} and repointed ${
                            rid != null ? `reader ${rid}` : "the box"
                          }`,
                        )
                      }
                    >
                      {busy === "readdress" ? "Applying…" : "Apply"}
                    </Button>
                    <button
                      type="button"
                      className="text-xs text-secondary-500 hover:underline"
                      onClick={() => setReaddressMac(null)}
                    >
                      cancel
                    </button>
                    <p className="text-[11px] text-warning-800 w-full">
                      Reboots the bridge (~10-15s) and also updates the box&apos;s own
                      reader address, because changing only one looks exactly like a dead
                      reader. The box-side update is scoped to
                      {rid != null ? ` reader ${rid}` : " this reader"} alone, so any other
                      reader on this box keeps its own address.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReaderAdvanced;
